/**
 * @format
 */
import { InjectionToken } from '@angular/core';
import {
    CompositeRule,
    CourseUnit,
    CourseUnitSubstitution,
    CustomCourseUnitAttainment,
    CustomModuleAttainment,
    CustomStudyDraft,
    EntityWithRule,
    GradeRaiseAttempt,
    LocalId,
    Module,
    OtmId,
} from 'common-typescript/types';
import { Subject } from 'rxjs';
import { PlanOperation } from '../plan-manager/plan-manager.service';

export enum UiOperationType {
    OPEN_CUSTOM_STUDY_DRAFT_CREATION_MODAL = 'OpenCustomStudyDraftCreationModal',
    OPEN_CUSTOM_STUDY_DRAFT_INFO_MODAL = 'OpenCustomStudyDraftInfoModal',
    OPEN_CUSTOM_ATTAINMENT_INFO_MODAL = 'OpenCustomAttainmentInfoModal',
    OPEN_MODULE = 'OpenModule',
    OPEN_MODULE_INFO_MODAL = 'OpenModuleInfoModal',
    OPEN_COURSE_UNIT_INFO_MODAL = 'OpenCourseUnitInfoModal',
    OPEN_SELECT_COMPLETION_METHOD_MODAL = 'OpenSelectCompletionMethodModal',
    OPEN_ATTAINMENT_DETAILS_MODAL = 'openAttainmentDetailsModal',
    OPEN_CUSTOM_COURSE_UNIT_ATTAINMENT_DETAILS_MODAL = 'openCustomCourseUnitAttainmentDetailsModal',
    OPEN_SELECT_COMPLETION_METHOD_WITH_GRADE_RAISE_ATTEMPT_MODAL = 'OpenSelectCompletionMethodWithGradeRaiseAttemptModal',
    OPEN_SELECT_SUBSTITUTION_MODAL = 'OpenSelectSubstitutionModal',
    CLOSE_AND_SAVE = 'CloseAndSave',
}

/** The target each UI operation type carries. Every operation type must have an entry here. */
interface UiOperationTargets {
    [UiOperationType.OPEN_CUSTOM_STUDY_DRAFT_CREATION_MODAL]: EntityWithRule;
    [UiOperationType.OPEN_CUSTOM_STUDY_DRAFT_INFO_MODAL]: CustomStudyDraft;
    [UiOperationType.OPEN_CUSTOM_ATTAINMENT_INFO_MODAL]: CustomCourseUnitAttainment;
    [UiOperationType.OPEN_MODULE]: EntityWithRule;
    [UiOperationType.OPEN_MODULE_INFO_MODAL]: EntityWithRule;
    [UiOperationType.OPEN_COURSE_UNIT_INFO_MODAL]: CourseUnit;
    [UiOperationType.OPEN_SELECT_COMPLETION_METHOD_MODAL]: CourseUnit;
    [UiOperationType.OPEN_ATTAINMENT_DETAILS_MODAL]: CourseUnit;
    [UiOperationType.OPEN_CUSTOM_COURSE_UNIT_ATTAINMENT_DETAILS_MODAL]: CustomCourseUnitAttainment;
    [UiOperationType.OPEN_SELECT_COMPLETION_METHOD_WITH_GRADE_RAISE_ATTEMPT_MODAL]: CourseUnit;
    [UiOperationType.OPEN_SELECT_SUBSTITUTION_MODAL]: CourseUnit;
    [UiOperationType.CLOSE_AND_SAVE]: null;
}

/** The target of the given UI operation type, e.g. `UiOperationTarget<UiOperationType.OPEN_MODULE>` is `EntityWithRule`. */
export type UiOperationTarget<T extends UiOperationType> = UiOperationTargets[T];

/** A UI operation; checking `uiOperationType` narrows `target` to that operation's target type. */
export type UiOperation = {
    [T in UiOperationType]: { uiOperationType: T; target: UiOperationTarget<T> };
}[UiOperationType];

export const PLAN_ACTIONS_SERVICE_INJECTION_TOKEN = new InjectionToken<PlanActionsService>('PlanActionsService');

/**
 * Refer to the README.md under the plan-structure folder for more information on the PlanActionsService or/and check the confluence page:
 * https://funidata.atlassian.net/wiki/x/BQA0F
 */
/**
 * Every operation is required, so an implementation that lacks one is a compile error instead of a call that silently does nothing.
 * The old plan UI uses the partial `LegacyPlanActionsService` instead.
 */
export interface PlanActionsService {
    planOperationSubject: Subject<PlanOperation>;
    uiOperationSubject: Subject<UiOperation>;

    openCustomStudyDraftCreationModal: (parentModule: EntityWithRule) => void;
    openCustomStudyDraftInfoModal: (customStudyDraft: CustomStudyDraft) => void;
    openCustomCourseUnitAttainmentInfoModal: (customCourseUnitAttainment: CustomCourseUnitAttainment) => void;
    openCourseUnitInfoModal: (courseUnit: CourseUnit) => void;
    openModuleInfoModal: (module: Module) => void;
    openModule: (module: EntityWithRule) => void;
    openSelectCompletionMethodModal: (courseUnit: CourseUnit) => void;
    openSelectCompletionMethodWithGradeRaiseAttemptModal: (courseUnit: CourseUnit) => void;
    openAttainmentDetailsModal: (courseUnit: CourseUnit) => void;
    /** Optional on purpose: substitution selection only renders where the host implements this, i.e. on the plan page. */
    openSelectSubstitutionModal?: (courseUnit: CourseUnit) => void;
    /** Optional on purpose: the custom course unit attainment details link only renders where the host implements this. */
    openCustomCourseUnitAttainmentDetailsModal?: (customCourseUnitAttainment: CustomCourseUnitAttainment) => void;

    selectCourseUnit: (courseUnit: CourseUnit, parentModule: EntityWithRule) => void;
    forceSelectCourseUnitById: (courseUnitId: OtmId, parentModule: EntityWithRule) => void;
    selectCourseUnitByGroupId: (courseUnitGroupId: OtmId, parentModule: EntityWithRule) => void;
    removeCourseUnit: (courseUnit: CourseUnit, parentModule: EntityWithRule) => void;
    forceRemoveCourseUnit: (courseUnit: CourseUnit, parentModule: EntityWithRule) => void;
    moveCourseUnit: (courseUnit: CourseUnit, parentModule: EntityWithRule) => void;
    selectModule: (module: EntityWithRule, parentModule: EntityWithRule) => void;
    forceSelectModuleById: (moduleId: OtmId, parentModule: EntityWithRule) => void;
    selectModuleByGroupId: (moduleGroupId: OtmId, parentModule: EntityWithRule) => void;
    removeModule: (module: EntityWithRule, parentModule: EntityWithRule) => void;
    forceRemoveModule: (module: Module, parentModule: EntityWithRule) => void;
    activateRuleGroup: (rule: CompositeRule, parentModule: EntityWithRule) => void;
    deactivateRuleGroup: (rule: CompositeRule, parentModule: EntityWithRule) => void;
    selectCustomCourseUnitAttainment: (customCourseUnitAttainment: CustomCourseUnitAttainment, parentModule: EntityWithRule) => void;
    removeCustomCourseUnitAttainmentById: (customCourseUnitAttainmentId: OtmId, parentModule: EntityWithRule) => void;
    selectCustomModuleAttainment: (customModuleAttainment: CustomModuleAttainment, parentModule: EntityWithRule) => void;
    removeCustomModuleAttainmentById: (customModuleAttainmentId: OtmId, parentModule: EntityWithRule) => void;
    addCustomStudyDraft: (customStudyDraft: CustomStudyDraft, parentModule: EntityWithRule) => void;
    removeCustomStudyDraft: (customStudyDraft: CustomStudyDraft, parentModule: EntityWithRule) => void;
    selectCompletionMethod: (courseUnit: CourseUnit, completionMethodId: LocalId) => void;
    addAssessmentItemSelection: (courseUnit: CourseUnit, assessmentItemId: OtmId) => void;
    removeAssessmentItemSelection: (courseUnit: CourseUnit, assessmentItemId: OtmId) => void;
    changeCourseUnitVersion: (oldCourseUnitVersion: CourseUnit, newCourseUnitVersion: CourseUnit) => void;
    removeGradeRaiseAttempt: (courseUnitId: OtmId) => void;
    addGradeRaiseAttempt: (courseUnitId: OtmId, gradeRaiseAttempt: GradeRaiseAttempt) => void;
    selectSubstitutions: (
        courseUnit: CourseUnit,
        substitutions: CourseUnitSubstitution[],
        substituteCourseUnitsByGroupId: { [key: OtmId]: CourseUnit },
    ) => void;
    removeSubstitutions: (courseUnit: CourseUnit) => void;
}

/**
 * The partial plan actions of the old plan UI in `lib/plan` (`GeneralPlanActionsService`), which is frozen until it is retired. Its
 * components call the operations with `?.()`, because the implementation lacks some of them.
 */
export type LegacyPlanActionsService = Pick<PlanActionsService, 'planOperationSubject' | 'uiOperationSubject'> &
    Partial<Omit<PlanActionsService, 'planOperationSubject' | 'uiOperationSubject'>> & {
        closeAndSave?: () => void;
    };
